import { createContext, useContext, useState, useRef, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { apiFetch, getValidToken } from '../utils/api';

const AudioContext = createContext();

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const [currentPost, setCurrentPost] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();

    audioRef.current.addEventListener('loadedmetadata', () => {
      // Only update duration if it's a valid number
      if (audioRef.current.duration && isFinite(audioRef.current.duration)) {
        setDuration(audioRef.current.duration);
      }
    });

    audioRef.current.addEventListener('ended', () => {
      handleNext();
    });

    audioRef.current.addEventListener('error', (e) => {
      console.error('Audio error:', e);
      setIsPlaying(false);
    });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Update current time
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying]);

  const playPost = async (post) => {
    if (!post) return;

    // If same post, toggle play/pause
    if (currentPost?.id === post.id) {
      togglePlayPause();
      return;
    }

    // Pause current audio before switching
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }

    // Load new post
    setCurrentPost(post);
    setCurrentTime(0);

    // Set duration from post object as fallback
    if (post.duration) {
      setDuration(post.duration);
    }

    if (audioRef.current) {
      try {
        // For replies with custom audio URL, no token needed (endpoint is public)
        // For posts, we need the token for the auth-gated audio endpoint
        let audioUrl;
        if (post._customAudioUrl) {
          audioUrl = post._customAudioUrl;
        } else {
          const token = await getValidToken();
          if (!token) return;
          audioUrl = `${BACKEND_URL}/api/posts/audio/${post.audio_file_id}?token=${token}`;
        }
        audioRef.current.src = audioUrl;
        audioRef.current.load();
        await audioRef.current.play();
        setIsPlaying(true);
        if (!post._isReply) {
          trackPlay(post.id);
        }
      } catch (error) {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
      }
    }
  };

  const trackPlay = async (postId) => {
    try {
      await apiFetch(`${BACKEND_URL}/api/posts/${postId}/play`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Error tracking play:', error);
    }
  };

  const togglePlayPause = async () => {
    if (!audioRef.current || !currentPost) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
      }
    }
  };

  const seek = (time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleNext = () => {
    if (queue.length > 0 && queueIndex < queue.length - 1) {
      const nextIndex = queueIndex + 1;
      setQueueIndex(nextIndex);
      playPost(queue[nextIndex]);
    } else {
      setIsPlaying(false);
    }
  };

  const handlePrevious = () => {
    if (currentTime > 3) {
      // If more than 3 seconds in, restart current track
      seek(0);
    } else if (queue.length > 0 && queueIndex > 0) {
      // Otherwise go to previous track
      const prevIndex = queueIndex - 1;
      setQueueIndex(prevIndex);
      playPost(queue[prevIndex]);
    } else {
      seek(0);
    }
  };

  const addToQueue = (post) => {
    setQueue([...queue, post]);
  };

  const playQueue = (posts, startIndex = 0) => {
    setQueue(posts);
    setQueueIndex(startIndex);
    playPost(posts[startIndex]);
  };

  // Play a full thread: [post, ...replies] sequentially
  const playThread = async (post, replies) => {
    const queueItems = [
      post,
      ...replies.map(r => ({
        ...r,
        _isReply: true,
        _customAudioUrl: `${BACKEND_URL}/api/replies/${r.id}/audio`,
      })),
    ];

    setQueue(queueItems);
    setQueueIndex(0);
    playPost(queueItems[0]);
  };

  const clearQueue = () => {
    setQueue([]);
    setQueueIndex(-1);
  };

  const removeFromQueue = (index) => {
    const newQueue = queue.filter((_, i) => i !== index);
    setQueue(newQueue);
    
    if (index < queueIndex) {
      setQueueIndex(queueIndex - 1);
    } else if (index === queueIndex) {
      // If removing current track, play next
      if (newQueue.length > 0) {
        const nextIndex = Math.min(queueIndex, newQueue.length - 1);
        setQueueIndex(nextIndex);
        playPost(newQueue[nextIndex]);
      } else {
        setCurrentPost(null);
        setIsPlaying(false);
        setQueueIndex(-1);
      }
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle if not typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowRight':
          e.preventDefault();
          seek(Math.min(currentTime + 5, duration));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seek(Math.max(currentTime - 5, 0));
          break;
        case 'n':
        case 'N':
          e.preventDefault();
          handleNext();
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          handlePrevious();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPost, isPlaying, currentTime, duration, queue, queueIndex]);

  const value = {
    currentPost,
    isPlaying,
    currentTime,
    duration,
    queue,
    queueIndex,
    playPost,
    togglePlayPause,
    seek,
    handleNext,
    handlePrevious,
    addToQueue,
    playQueue,
    playThread,
    clearQueue,
    removeFromQueue,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

